// src/features/portfolio-optimization/PortfolioOptimizationPage.tsx
//
// Portfolio Optimizer "Glass-Box" dashboard. Fetches the full
// Black-Litterman target-weight matrix (ALL five risk bands) in ONE call
// from GET /api/v1/portfolio/matrix, then lets the user drag a Risk Slider
// to switch profiles INSTANTLY — every chart/table recomputes client-side
// (the "pre-fetch & slide" architecture), so the slider never touches the
// network. Beyond the pie chart it surfaces the underlying ML conviction
// (Phase-1 signal z-scores) and volatility that justify each position.
//
// Reuses the standard <Layout sidebar={<Sidebar />}> shell so it sits
// behind the same auth gate / navigation as the rest of the suite.

import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Zap } from 'lucide-react';

import { Layout, Sidebar } from '../../shared/ui';
import {
    fetchMlInsights,
    fetchPortfolioMatrix,
    fetchStockRecommendations,
    type MlInsightsResponse,
    type PortfolioMatrixResponse,
    type StockRecommendationResponse,
} from './api';
import { ActiveTiltWaterfall } from './components/ActiveTiltWaterfall';
import { AssetTearsheet, type RadarAxis } from './components/AssetTearsheet';
import { ConvictionTable } from './components/ConvictionTable';
import { HoldingsTable } from './components/HoldingsTable';
import { PortfolioProjectionChart } from './components/PortfolioProjectionChart';
import {
    RiskReturnScatter,
    type RiskReturnPoint,
} from './components/RiskReturnScatter';
import { SectorDonut, type SectorSlice } from './components/SectorDonut';
import { TacticalRecommendationsZone } from './components/TacticalRecommendationsZone';

// Percentile rank (0..100) of `value` within `arr`. Used to normalise the
// tearsheet radar axes "vs the active book" — robust to outliers / scale.
const percentileRank = (value: number, arr: number[]): number => {
    if (arr.length <= 1) return 50;
    const below = arr.filter((v) => v < value).length;
    const equal = arr.filter((v) => v === value).length;
    return ((below + 0.5 * equal) / arr.length) * 100;
};

// Strict slider order — index 0..4 maps low → high risk.
const RISK_PROFILES = [
    'Capital Preservation',
    'Conservative',
    'Balanced',
    'Growth',
    'Aggressive',
] as const;

const SLIDER_LABELS = [
    'Capital Pres.',
    'Conservative',
    'Balanced',
    'Growth',
    'Aggressive',
];

interface KpiCardProps {
    label: string;
    value: string;
    accent?: string;
}

const KpiCard = ({ label, value, accent }: KpiCardProps) => (
    <div className="surface-card !p-4">
        <p className="muted text-xs uppercase tracking-wider font-semibold">
            {label}
        </p>
        <p className={`mt-1 text-2xl font-black tracking-tight ${accent ?? 'text-slate-900 dark:text-white'}`}>
            {value}
        </p>
    </div>
);

export const PortfolioOptimizationPage = () => {
    const [data, setData] = useState<PortfolioMatrixResponse | null>(null);
    const [mlData, setMlData] = useState<MlInsightsResponse | null>(null);
    const [recData, setRecData] = useState<StockRecommendationResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [riskLevel, setRiskLevel] = useState<number>(2); // default Balanced
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
    const [activeSection, setActiveSection] = useState<string>('high-conviction-ideas');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Pre-fetch ALL payloads once. The matrix is required; the ML
                // X-ray and Recommendations degrade gracefully if absent.
                const [matrix, ml, recs] = await Promise.all([
                    fetchPortfolioMatrix('stock_ranking'),
                    fetchMlInsights('stock_ranking').catch(() => null),
                    fetchStockRecommendations('stock_ranking').catch(() => null),
                ]);
                if (!cancelled) {
                    setData(matrix);
                    setMlData(ml);
                    setRecData(recs);
                    setError(null);
                }
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load portfolio matrix', err);
                setError(
                    'Unable to load the optimised portfolio right now. The nightly '
                        + 'rebalance may still be warming up — please try again shortly.',
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const activeProfileName = RISK_PROFILES[riskLevel];
    const activeHoldings = useMemo(
        () => data?.profiles[activeProfileName]?.assets ?? [],
        [data, activeProfileName],
    );

    // Roll per-asset weights up into sector buckets for the donut.
    const sectorData = useMemo<SectorSlice[]>(() => {
        const buckets: Record<string, number> = {};
        activeHoldings.forEach((h) => {
            buckets[h.sector] = (buckets[h.sector] ?? 0) + h.target_weight;
        });
        return Object.entries(buckets)
            .map(([name, value]) => ({
                name,
                value: Number((value * 100).toFixed(2)),
            }))
            .sort((a, b) => b.value - a.value);
    }, [activeHoldings]);

    // Glass-box KPIs: concentration, dominant sector, conviction + risk.
    const kpis = useMemo(() => {
        const count = activeHoldings.length;
        const maxWeight = activeHoldings.reduce(
            (m, h) => Math.max(m, h.target_weight),
            0,
        );
        const topSector = sectorData[0]?.name ?? '—';

        let wSum = 0;
        let convNum = 0;
        let volNum = 0;
        activeHoldings.forEach((h) => {
            wSum += h.target_weight;
            if (h.forge_ultra_signal !== null) {
                convNum += h.target_weight * h.forge_ultra_signal;
            }
            if (h.annualized_volatility !== null) {
                volNum += h.target_weight * h.annualized_volatility;
            }
        });
        const avgConviction = wSum > 0 ? convNum / wSum : 0;
        const weightedVol = wSum > 0 ? volNum / wSum : 0;
        return { count, maxWeight, topSector, avgConviction, weightedVol };
    }, [activeHoldings, sectorData]);

    // --- ML "Quant X-Ray" derived data (Zones 2 & 3) ---------------------
    const signals = useMemo(() => mlData?.signals ?? [], [mlData]);

    // Active-book weight lookup (per the selected risk profile).
    const holdingBySymbol = useMemo(() => {
        const m = new Map<string, (typeof activeHoldings)[number]>();
        activeHoldings.forEach((h) => m.set(h.symbol, h));
        return m;
    }, [activeHoldings]);

    // Risk/Return scatter: join each ML view with the active-book volatility
    // & weight (only names actually held, since volatility lives on the
    // matrix, not the X-ray).
    const scatterPoints = useMemo<RiskReturnPoint[]>(() => {
        const pts: RiskReturnPoint[] = [];
        signals.forEach((s) => {
            const h = holdingBySymbol.get(s.symbol);
            if (!h || h.annualized_volatility === null) return;
            pts.push({
                symbol: s.symbol,
                volatility: Number((h.annualized_volatility * 100).toFixed(2)),
                expectedReturn: Number((s.expected_return_q * 100).toFixed(2)),
                weight: Number((h.target_weight * 100).toFixed(2)),
                probability: s.master_probability,
                side: s.ml_side,
            });
        });
        return pts;
    }, [signals, holdingBySymbol]);

    // Per-symbol radar axes, percentile-normalised across the X-ray set.
    const radarBySymbol = useMemo(() => {
        const m = new Map<string, RadarAxis[]>();
        if (!signals.length) return m;
        const probs = signals.map((s) => s.master_probability);
        const edges = signals.map((s) => Math.abs(s.expected_return_q));
        const confs = signals.map((s) => 1 / Math.max(s.uncertainty_omega, 1e-9));
        const zs = signals.map((s) => Math.abs(s.phase_1_z_score ?? 0));
        const wts = signals.map((s) => holdingBySymbol.get(s.symbol)?.target_weight ?? 0);
        signals.forEach((s, i) => {
            m.set(s.symbol, [
                { axis: 'Conviction', value: Math.round(percentileRank(probs[i], probs)) },
                { axis: 'Edge', value: Math.round(percentileRank(edges[i], edges)) },
                { axis: 'Confidence', value: Math.round(percentileRank(confs[i], confs)) },
                { axis: 'Signal', value: Math.round(percentileRank(zs[i], zs)) },
                { axis: 'Weight', value: Math.round(percentileRank(wts[i], wts)) },
            ]);
        });
        return m;
    }, [signals, holdingBySymbol]);

    const selectedSignal = useMemo(
        () => signals.find((s) => s.symbol === selectedSymbol) ?? null,
        [signals, selectedSymbol],
    );

    // Sticky in-page navigation tabs. The "Tactical Daily Signals" tab is
    // only surfaced when the recommendation engine actually returned rows,
    // so the anchor it points at is guaranteed to exist in the DOM.
    const navSections = useMemo(() => {
        const items: { id: string; label: string; icon: typeof Sparkles }[] = [
            {
                id: 'high-conviction-ideas',
                label: 'High Conviction Ideas',
                icon: Sparkles,
            },
        ];
        if (recData && recData.recommendations.length > 0) {
            items.push({
                id: 'tactical-daily-signals',
                label: 'Tactical Daily Signals',
                icon: Zap,
            });
        }
        return items;
    }, [recData]);

    const scrollToSection = (id: string) => {
        const el = document.getElementById(id);
        if (!el) return;
        setActiveSection(id);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Scroll-spy: highlight the tab whose section is currently in view.
    // Root is the viewport (the whole SPA scrolls on <body>); the negative
    // top rootMargin accounts for the sticky app header + this tab bar.
    useEffect(() => {
        if (loading || error || !data) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveSection(entry.target.id);
                });
            },
            { rootMargin: '-140px 0px -55% 0px', threshold: 0 },
        );
        navSections.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [loading, error, data, navSections]);

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Forge Ultra's Optimized Portfolio"
            subtitle="Black-Litterman methodology — target allocations across the full risk spectrum."
        >
            {loading ? (
                <div className="space-y-6">
                    <div className="surface-card-skeleton h-28" />
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="surface-card-skeleton h-[28rem] lg:col-span-1" />
                        <div className="surface-card-skeleton h-[28rem] lg:col-span-2" />
                    </div>
                </div>
            ) : error ? (
                <div className="surface-card-error">{error}</div>
            ) : !data ? null : (
                <>
                    {/* Freshness badge */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <span className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-1.5 rounded-full">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            Optimised Portfolio Holdings
                        </span>
                        <span className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800/50 px-2 py-1 rounded">
                            As of {data.target_date}
                        </span>
                    </div>

                    {/* STICKY IN-PAGE NAVIGATION — jump straight to the two
                        signal decks. Tucks just under the global app header. */}
                    <nav className="sticky top-[68px] z-20 mb-6">
                        <div className="flex items-center gap-1.5 p-1.5 w-full sm:w-fit rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-slate-200/80 dark:border-slate-700/70 shadow-lg shadow-slate-900/5 ring-1 ring-black/5">
                            {navSections.map((s) => {
                                const active = activeSection === s.id;
                                const Icon = s.icon;
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => scrollToSection(s.id)}
                                        className={`relative flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${
                                            active
                                                ? 'text-white'
                                                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                                        }`}
                                    >
                                        {active && (
                                            <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-emerald-500 via-emerald-500 to-teal-500 shadow-md shadow-emerald-500/40" />
                                        )}
                                        <Icon
                                            className={`relative w-4 h-4 shrink-0 ${
                                                active ? 'text-white' : 'text-emerald-500'
                                            }`}
                                        />
                                        <span className="relative whitespace-nowrap">
                                            {s.label}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </nav>

                    {/* RISK SLIDER — the control panel. */}
                    <div className="surface-card mb-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="section-title">Choose Your Own Risk Tolerance to Get Optimized Portfolio: Weights and Allocation</h2>
                            <span className="px-4 py-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-bold rounded-full text-sm tracking-wide">
                                {activeProfileName.toUpperCase()}
                            </span>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={4}
                            step={1}
                            value={riskLevel}
                            onChange={(e) => setRiskLevel(parseInt(e.target.value, 10))}
                            className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                            aria-label="Risk tolerance"
                        />
                        <div className="flex justify-between text-[11px] sm:text-xs text-slate-400 dark:text-slate-500 font-bold mt-3 px-1 uppercase tracking-wider">
                            {SLIDER_LABELS.map((label, idx) => (
                                <span
                                    key={label}
                                    className={
                                        idx === riskLevel
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : idx === 1 || idx === 3
                                              ? 'hidden sm:inline'
                                              : ''
                                    }
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* KPI strip — the "glass box" headline metrics. */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <KpiCard label="Holdings" value={String(kpis.count)} />
                        <KpiCard label="Top Sector" value={kpis.topSector} />
                        <KpiCard
                            label="Avg ML Conviction"
                            value={`${kpis.avgConviction > 0 ? '+' : ''}${kpis.avgConviction.toFixed(2)}σ`}
                            accent={
                                kpis.avgConviction >= 0
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : 'text-rose-600 dark:text-rose-400'
                            }
                        />
                        <KpiCard
                            label="Weighted Volatility"
                            value={`${(kpis.weightedVol * 100).toFixed(1)}%`}
                        />
                    </div>

                    {/* DASHBOARD GRID: Visuals + Holdings. */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left Column: Donut + Projection Chart */}
                        <div className="lg:col-span-1 flex flex-col gap-6">
                            <div className="surface-card flex flex-col">
                                <h3 className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-6">
                                    Sector Exposures
                                </h3>
                                <SectorDonut data={sectorData} />
                            </div>

                            {/* Forward-Looking Performance Simulator */}
                            <PortfolioProjectionChart
                                weightedVol={kpis.weightedVol}
                                avgConviction={kpis.avgConviction}
                            />
                        </div>

                        <div className="lg:col-span-2 surface-card !p-0 overflow-hidden flex flex-col">
                            <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                <h3 className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                    Target Holdings
                                </h3>
                                <span className="text-xs font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 px-3 py-1 rounded-full shadow-sm">
                                    {activeHoldings.length} Assets
                                </span>
                            </div>
                            <HoldingsTable
                                holdings={activeHoldings}
                                maxWeight={kpis.maxWeight}
                            />
                        </div>
                    </div>

                    {/* ============================================================
                        ZONE 2 — The Institutional "Tilt" (Wealth Manager View)
                        ============================================================ */}
                    <div className="mt-10 mb-4">
                        <h2 className="text-lg font-black text-slate-900 dark:text-white">
                            Optimized Allocation & Tilt
                        </h2>
                        <p className="muted text-sm">
                            Where the optimiser is leaning, and whether the risk is
                            paying for an edge.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="surface-card flex flex-col">
                            <h3 className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4">
                                Active Allocations in Optimized Portfolio
                            </h3>
                            <ActiveTiltWaterfall holdings={activeHoldings} />
                        </div>
                        <div className="surface-card flex flex-col">
                            <h3 className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4">
                                Risk / Return — Top Recommendations for Your Portfolio vs Volatility
                            </h3>
                            <RiskReturnScatter points={scatterPoints} />
                        </div>
                    </div>

                    {/* ============================================================
                        ZONE 3 — The ML "Glass Box" (The Quant Lab)
                        ============================================================ */}
                    <div id="high-conviction-ideas" className="scroll-mt-36 mt-10 mb-4">
                        <h2 className="text-lg font-black text-slate-900 dark:text-white">
                            High Conviction Buy and Sell Ideas Based on Advanced ML Forecast
                        </h2>
                        <p className="muted text-sm">
                            Verified using advanced Black-Litterman methodology with 3-years backtesting —
                            click a row to open the asset info-sheet.
                        </p>
                    </div>
                    <div className="surface-card !p-0 overflow-hidden flex flex-col">
                        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                Weights &amp; Conviction
                            </h3>
                            <span className="text-xs font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 px-3 py-1 rounded-full shadow-sm">
                                {signals.length} Active Recommendations
                            </span>
                        </div>
                        <ConvictionTable
                            signals={signals}
                            selectedSymbol={selectedSymbol}
                            onSelect={setSelectedSymbol}
                        />
                    </div>

                    {/* ============================================================
                        ZONE 4 — Daily Tactical Recommendations
                        ============================================================ */}
                    {recData && recData.recommendations.length > 0 && (
                        <div id="tactical-daily-signals" className="scroll-mt-36">
                            <TacticalRecommendationsZone 
                                recommendations={recData.recommendations} 
                                onSelectSymbol={setSelectedSymbol} 
                            />
                        </div>
                    )}

                    {/* Slide-out asset tearsheet */}
                    <AssetTearsheet
                        open={selectedSymbol !== null}
                        onClose={() => setSelectedSymbol(null)}
                        signal={selectedSignal}
                        symbol={selectedSymbol}
                        holding={
                            selectedSymbol
                                ? holdingBySymbol.get(selectedSymbol)
                                : undefined
                        }
                        radar={
                            selectedSymbol
                                ? radarBySymbol.get(selectedSymbol) ?? []
                                : []
                        }
                    />
                </>
            )}
        </Layout>
    );
};

export default PortfolioOptimizationPage;
